import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, FileText, Plus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatDate, formatMoney } from '@/lib/format';
import { useQuotes } from './api';
import { QUOTE_STATUS_LABELS, QUOTE_STATUS_VARIANT } from './labels';
import type { Quote, QuoteStatus } from './types';

const STATUSES = Object.keys(QUOTE_STATUS_LABELS) as QuoteStatus[];

export function QuotesPage() {
  const pagination = usePagination();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('');

  const debounced = useDebounce(search);
  const { data, isLoading, error, refetch } = useQuotes({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    sort: sort || undefined,
  });

  const columns: Column<Quote>[] = [
    {
      key: 'number',
      header: 'Quote',
      sortKey: 'number',
      cell: (row) => (
        <div className="min-w-0">
          <div className="font-medium">
            {row.number}
            {row.version > 1 && <span className="text-muted-foreground"> v{row.version}</span>}
          </div>
          <div className="truncate text-xs text-muted-foreground">{row.company.name}</div>
        </div>
      ),
    },
    {
      key: 'deal',
      header: 'Deal',
      className: 'text-sm',
      cell: (row) =>
        row.deal ? (
          <Link
            to={`/crm/deals/${row.deal.id}`}
            onClick={(event) => event.stopPropagation()}
            className="hover:underline"
          >
            {row.deal.number}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'total',
      header: 'Total',
      sortKey: 'grand_total',
      className: 'tabular text-sm font-medium',
      cell: (row) => formatMoney(row.grandTotal, row.currency),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      cell: (row) => (
        <Badge variant={QUOTE_STATUS_VARIANT[row.status]}>{QUOTE_STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      key: 'views',
      header: 'Opens',
      className: 'text-sm text-muted-foreground',
      cell: (row) =>
        row.viewCount > 0 ? (
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            {row.viewCount}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'issued',
      header: 'Issued',
      sortKey: 'issue_date',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.issueDate),
    },
    {
      key: 'valid',
      header: 'Valid until',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => formatDate(row.validUntil),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Quotes"
        description="Proposals sent to clients, with a link they can accept from."
        actions={
          <Can permission="crm.quote.create">
            <Button onClick={() => navigate('/crm/quotes/new')}>
              <Plus className="h-4 w-4" />
              New quote
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search number or company…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {QUOTE_STATUS_LABELS[value]}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load quotes.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => navigate(`/crm/quotes/${row.id}`)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No quotes yet"
        emptyDescription="Build a proposal from a deal, or start one from scratch."
        emptyAction={
          <Can permission="crm.quote.create">
            <Button onClick={() => navigate('/crm/quotes/new')}>
              <FileText className="h-4 w-4" />
              New quote
            </Button>
          </Can>
        }
      />
    </div>
  );
}
